// src/components/ecommerce/ServicesManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus,
  Search,
  Filter,
  Grid,
  List,
  Edit,
  Trash2,
  Eye,
  Copy,
  Upload,
  Download,
  Star,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Tag,
  DollarSign,
  MapPin,
  Calendar,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Loader2,
  Award,
  MessageCircle
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { ServiceForm } from './ServiceForm';
import { getStoreTypeConfig } from './utils/storeTypeConfig';
import toast from 'react-hot-toast';

export const ServicesManager = ({ storeId, storeData, onUpdate }) => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedServices, setSelectedServices] = useState([]);
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deliveryFilter, setDeliveryFilter] = useState('all');

  // Get store type configuration
  const storeType = storeData?.storeType || 'services';
  const config = useMemo(() => getStoreTypeConfig(storeType), [storeType]);

  useEffect(() => {
    loadServices();
    loadCategories();
  }, [storeId]);

  const loadServices = async () => {
    setLoading(true);
    try {
      const servicesData = await ecommerceService.getProducts(storeId); // Services stored as products
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error(`Failed to load ${config.itemNamePlural.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await ecommerceService.getCategories(storeId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowServiceForm(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm(`Delete this ${config.itemName.toLowerCase()}?`)) return;

    try {
      await ecommerceService.deleteProduct(storeId, serviceId);
      await loadServices();
      toast.success(`${config.itemName} deleted successfully`);
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(`Failed to delete ${config.itemName.toLowerCase()}`);
    }
  };

  const handleDuplicateService = async (service) => {
    try {
      const { id, createdAt, updatedAt, ...serviceData } = service;
      await ecommerceService.addProduct(storeId, {
        ...serviceData,
        name: `${serviceData.name} (Copy)`,
        status: 'draft'
      });
      await loadServices();
      toast.success(`${config.itemName} duplicated successfully`);
      onUpdate?.();
    } catch (error) {
      console.error('Error duplicating service:', error);
      toast.error(`Failed to duplicate ${config.itemName.toLowerCase()}`);
    }
  };

  const handleServiceSave = async () => {
    await loadServices();
    setShowServiceForm(false);
    setEditingService(null);
    toast.success(`${config.itemName} saved successfully`);
    onUpdate?.();
  };

  // Filter and sort services
  const filteredServices = useMemo(() => {
    let filtered = [...services];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.name?.toLowerCase().includes(query) ||
        service.description?.toLowerCase().includes(query) ||
        service.category?.toLowerCase().includes(query) ||
        service.deliveryMethod?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Delivery method filter
    if (deliveryFilter !== 'all') {
      filtered = filtered.filter(s => s.deliveryMethod === deliveryFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'price':
          comparison = (a.price || 0) - (b.price || 0);
          break;
        case 'duration':
          comparison = (a.duration || '').localeCompare(b.duration || '');
          break;
        case 'created':
        default:
          comparison = (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [services, searchQuery, selectedCategory, statusFilter, deliveryFilter, sortBy, sortOrder]);

  // Get unique delivery methods
  const deliveryMethods = useMemo(() => {
    return [...new Set(services.map(s => s.deliveryMethod).filter(Boolean))];
  }, [services]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'draft': return 'text-yellow-600 bg-yellow-100';
      case 'sold_out': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <config.icon className="h-6 w-6 text-blue-600" />
            {config.itemNamePlural}
          </h2>
          <p className="text-gray-600 mt-1">
            {filteredServices.length} of {services.length} {config.itemNamePlural.toLowerCase()}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => ecommerceService.exportProducts(storeId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
          
          <button
            onClick={handleAddService}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {config.addItemLabel}
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${config.itemNamePlural.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>

            {/* Delivery Method Filter */}
            {deliveryMethods.length > 0 && (
              <select
                value={deliveryFilter}
                onChange={(e) => setDeliveryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Locations</option>
                {deliveryMethods.map(method => (
                  <option key={method} value={method}>
                    {method.replace('-', ' ').split(' ').map(w => 
                      w.charAt(0).toUpperCase() + w.slice(1)
                    ).join(' ')}
                  </option>
                ))}
              </select>
            )}

            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort);
                setSortOrder(order);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="created-desc">Newest First</option>
              <option value="created-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="price-asc">Price Low-High</option>
              <option value="price-desc">Price High-Low</option>
              <option value="duration-asc">Duration Short-Long</option>
              <option value="duration-desc">Duration Long-Short</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Services Display */}
      {filteredServices.length > 0 ? (
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
        }>
          {filteredServices.map((service) => (
            <ServiceManagerCard
              key={service.id}
              service={service}
              config={config}
              viewMode={viewMode}
              getStatusColor={getStatusColor}
              onEdit={() => handleEditService(service)}
              onDelete={() => handleDeleteService(service.id)}
              onDuplicate={() => handleDuplicateService(service)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <config.icon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{config.emptyMessage}</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || selectedCategory !== 'all' || statusFilter !== 'all' || deliveryFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : config.emptyMessageDetail
            }
          </p>
          <button
            onClick={handleAddService}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {config.addItemLabel}
          </button>
        </div>
      )}

      {/* Service Form Modal */}
      {showServiceForm && (
        <ServiceForm
          service={editingService}
          storeId={storeId}
          storeData={storeData}
          storeTypeConfig={config}
          categories={categories}
          isOpen={showServiceForm}
          onClose={() => {
            setShowServiceForm(false);
            setEditingService(null);
          }}
          onSave={handleServiceSave}
        />
      )}
    </div>
  );
};

// Service Manager Card Component
const ServiceManagerCard = ({ 
  service, 
  config,
  viewMode, 
  getStatusColor,
  onEdit, 
  onDelete,
  onDuplicate
}) => {
  const Icon = config.icon;

  if (viewMode === 'list') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
            {service.images?.[0] ? (
              <img
                src={service.images[0]}
                alt={service.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon className="h-10 w-10 text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 truncate">{service.name}</h3>
                {service.category && (
                  <span className="text-xs text-gray-500">{service.category}</span>
                )}
              </div>
              {service.price && (
                <span className="text-lg font-bold text-red-600 ml-4">
                  R{service.price}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{service.description}</p>

            <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
              {service.duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{service.duration}</span>
                </div>
              )}
              {service.deliveryMethod && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="capitalize">{service.deliveryMethod.replace('-', ' ')}</span>
                </div>
              )}
              <span className={`px-2 py-0.5 rounded-full ${getStatusColor(service.status)}`}>
                {service.status}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={onDuplicate}
              className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
              title="Duplicate"
            >
              <Copy className="h-4 w-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      <div className="aspect-video bg-gray-200 relative">
        {service.images?.[0] ? (
          <img
            src={service.images[0]}
            alt={service.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="h-16 w-16 text-gray-400" />
          </div>
        )}
        {service.featured && (
          <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <Star className="h-3 w-3" />
            Featured
          </div>
        )}
        <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${getStatusColor(service.status)}`}>
          {service.status}
        </span>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate mb-1">{service.name}</h3>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{service.description}</p>
        
        <div className="space-y-2 mb-3">
          {service.price && (
            <div className="text-lg font-bold text-red-600">
              R{service.price}
            </div>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {service.duration && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{service.duration}</span>
              </div>
            )}
            {service.deliveryMethod && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="capitalize">{service.deliveryMethod.replace('-', ' ')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={onDuplicate}
            className="bg-green-100 text-green-700 py-2 px-3 rounded-lg hover:bg-green-200"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};